// AdminBookings.jsx (FilteredBookings)

import React, { useState, useEffect, useCallback, useRef } from "react";
import moment from "moment";
import { useMutation } from "@apollo/client";
import { GET_BOOKINGS, DELETE_BOOKING } from "./graphql";
import BookingForm from "./BookingForm";
import DashboardBookingTable from "./DashboardBookingTable";

/* ----------------------------
   Helpers
---------------------------- */

const normalizeToDateString = (val) => {
  if (!val) return null;

  if (typeof val === "object" && moment.isMoment(val)) {
    return val.format("YYYY-MM-DD");
  }

  const s = String(val);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const m = moment(s);
  return m.isValid() ? m.format("YYYY-MM-DD") : null;
};

const serializeFilter = (f = {}) => {
  const statusArr = Array.isArray(f.status) ? f.status.filter(Boolean) : [];
  const uniqueSortedStatus = Array.from(new Set(statusArr.map(String))).sort();

  return {
    employeeId: f.employeeId || null,
    employeeIds: f.employeeIds || [], // include multi-select employee IDs if needed
    status: uniqueSortedStatus,
    verificationStatus: f.verificationStatus || null,
    queryStartDate: normalizeToDateString(f.queryStartDate),
    queryEndDate: normalizeToDateString(f.queryEndDate),
    customerType: f.customerType || null,
    isAdminToEmpFeedbackCompleted: f.isAdminToEmpFeedbackCompleted, // 🔥 backend filter
    funnelDropStage: f.funnelDropStage || null,
    declineCategory: f.declineCategory || null,
    cancelledByType: f.cancelledByType || null,
  };
};

/* ----------------------------
   Component
---------------------------- */

const FilteredBookings = ({ filters, onBack }) => {
  const [data, setData] = useState([]);
  const [editBookingForm, setEditBookingForm] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [getBookingsMutation] = useMutation(GET_BOOKINGS);
  const [deleteBooking] = useMutation(DELETE_BOOKING);

  const lastFetchFilterRef = useRef(null);

  const openEditForm = useCallback((booking) => {
    setEditingData(booking || null);
    setTimeout(() => setEditBookingForm(true), 0);
  }, []);

  const closeEditForm = useCallback(() => {
    setEditBookingForm(false);
  }, []);

  const handleDeleteClick = useCallback(
    async (booking) => {
      const id = booking?.id;
      if (!id) return;

      const confirmDelete = window.confirm(
        `Delete booking ${booking.bookingId}?`,
      );
      if (!confirmDelete) return;

      try {
        const res = await deleteBooking({
          variables: { data: id },
        });

        if (res?.data?.deleteBookings?.status === "SUCCESS") {
          setData((prev) => prev.filter((b) => b.id !== id));
        }
      } catch (err) {
        console.error("Delete failed:", err);
      }
    },
    [deleteBooking],
  );

  const handleOrderUpdated = useCallback((updatedOrder) => {
    if (!updatedOrder) return;

    setData((prev) =>
      prev.map((b) =>
        b.id === updatedOrder.id ? { ...b, ...updatedOrder } : b,
      ),
    );
  }, []);

  const loadBookings = useCallback(
    async (filter) => {
      if (!filter) return;

      setLoading(true);

      try {
        const serialized = serializeFilter(filter);
        const sNow = JSON.stringify(serialized);

        if (lastFetchFilterRef.current === sNow) {
          setLoading(false);
          return;
        }

        lastFetchFilterRef.current = sNow;

        const res = await getBookingsMutation({
          variables: { data: serialized },
        });

        if (res?.data?.getBookings?.status === "SUCCESS") {
          setData(res.data.getBookings.orders || []);
        } else {
          setData([]);
          if (res?.data?.getBookings?.message) alert(res.data.getBookings.message);
        }
      } catch (err) {
        console.error("Failed to load bookings", err);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [getBookingsMutation],
  );

  useEffect(() => {
    if (filters) {
      loadBookings(filters);
    }
  }, [filters, loadBookings]);

  return (
    <div className="w-full p-6">
      {editBookingForm ? (
        <BookingForm
          setEditBookingForm={closeEditForm}
          editingData={editingData}
          onBookingUpdated={handleOrderUpdated}
        />
      ) : (
        <DashboardBookingTable
          data={data}
          loading={loading}
          onBack={onBack}
          openEditForm={openEditForm}
          onDelete={handleDeleteClick}
          showReasonColumn={filters?.status?.includes("Declined") || false} // show reason column if we're filtering cancelled bookings
          showVerifiedAtColumn={["VERIFIED", "PAYMENT_VERIFIED", "PAYMENT_VERIFICATION_PENDING"].includes(filters?.verificationStatus)}
        />
      )}
    </div>
  );
};

export default FilteredBookings;
